import { memo } from "react";
import { useTranslation } from "react-i18next";
import { SystemReactTinyPopover } from "../../../../components/UIToolkit/Popups/SystemReactTinyPopover";
import { CoverageCellPopover } from "../CoverageCellPopover";
import { mapCalendarCells } from "../gridGeometry";
import { coverageRowLabel, type CoverageRow as RowType } from "../types";
import { type GridCellKey, useCoverageGridContext } from "./CoverageGridContext";
import { DetailCell } from "./DetailCell";
import { WeekDividerTrack } from "./WeekDividerTrack";

type Props = {
	row: RowType;
	draggingId?: string;
	isDragging: boolean;
};

/**
 * The day-cell loop of a Coverage · Detail row — a droppable `DetailCell` per day
 * with the single-open popover wrapping only the open/closing cell. Split out of
 * `DetailRow`; grid-wide values come from context, so props are just the row plus
 * the current drag state (which changes only on drag start/end, never on hover).
 */
export const DetailRowDays = memo(({ row, draggingId, isDragging }: Props) => {
	const { t } = useTranslation();
	const { weeks, dayDates, editWiring, popover, onOpenRecurring } = useCoverageGridContext();
	// Job depts identify a row by its job (localized "Unassigned" for the sentinel); role label for non-job rows.
	const rowLabel = `${row.location} · ${coverageRowLabel(row, t)} · ${row.shift}`;
	// Editable unless blocked or an outside-config row (no backing recurring shift to map).
	const recurringEditable = !!editWiring && !editWiring.editBlocked && !row.outsideConfig;

	return (
		<>
			{mapCalendarCells(
				weeks,
				(w, _d, idx) => {
					const cell = row.days[idx];
					const key: GridCellKey = { rowId: row.id, dayIdx: idx };
					const isOpen = popover.isOpen(key);
					// A closing cell keeps its wrapper mounted (isOpen=false) so the exit animation plays.
					const isClosing = popover.isClosing(key);
					const dayLabel = dayDates[idx].toFormat("ccc, LLL d");

					const cellBody = (
						<DetailCell
							rowId={row.id}
							dayIdx={idx}
							cell={cell}
							weekIdx={w}
							rowLabel={rowLabel}
							dayLabel={dayLabel}
							draggingId={draggingId}
							isDragging={isDragging}
							onEditAssignment={recurringEditable ? (assignment) => onOpenRecurring({ mode: "edit", row, nurseId: assignment.nurseId }, idx) : undefined}
							onCreate={recurringEditable ? () => onOpenRecurring({ mode: "create", row }, idx) : undefined}
							onOpen={() => popover.toggle(key)}
						/>
					);

					return isOpen || isClosing ? (
						<SystemReactTinyPopover
							isOpen={isOpen}
							onClose={popover.close}
							onClickOutside={popover.close}
							onExitComplete={() => popover.clearClosing(key)}
							positions={["top", "bottom"]}
							content={<CoverageCellPopover rowLabel={rowLabel} dayLabel={dayLabel} cell={cell} onClose={popover.close} />}
							childrenWrapperStyle={{ display: "block", width: "100%", height: "100%" }}
						>
							{cellBody}
						</SystemReactTinyPopover>
					) : (
						cellBody
					);
				},
				() => (
					<WeekDividerTrack />
				),
			)}
		</>
	);
});
